import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';

import Global from '../../../store/global-selectors';
import { DocumentType } from '../../../enums/document-type';
import { useModal } from '../../../hooks/use-modal';
import DocumentFormatColumn from './document-format-column';
import { Segment } from '../../../services/analytics-services/segment';
import { useUserHasConfigurationRole } from '../../../hooks/use-user-role';
import { PRINTING_SETUP_START, DOCUMENT_OPTIONS_CONFIG_START } from '../../../constants/analytics-events';
import docTypeToOptionsModalMap from './document-option-modal/doc-type-to-modal-map';
import { PrintToModal } from './print-to-modal';
import { DocumentOptionsModal } from './document-option-modal';

import messages, { printToOptionMessages } from './messages';
import styles from './styles.module.less';

const PrintSettingRow = ({ docType }) => {
  const printToSettings = useSelector(Global.selectPrintSettingsPrintSettings) ?? {};
  const userHasConfigurationRole = useUserHasConfigurationRole();
  const { open: openPrintToModal, isOpen: isPrintToModalOpen, close: closePrintToModal } = useModal();
  const { open: openDocumentOptionsModal, isOpen: isDocumentOptionsModalOpen, close: closeDocumentOptionsModal } = useModal();
  const { documentNameTranslation, printSettingKey, identifier } = docType;
  const { id, name: printToSettingName, workstationId } = printToSettings[printSettingKey] || {};
  const isConnect = !!id && !!workstationId; // preview and download only have a name property. if we have these it's connect.
  const printToMessage = printToOptionMessages[printToSettingName] || messages.alwaysPrompt;

  const onOpenPrintToModal = useCallback(() => {
    openPrintToModal();
    Segment.trackEvent(PRINTING_SETUP_START, {
      docType,
    });
  }, [docType, openPrintToModal]);

  const onOpenDocumentOptionsModal = useCallback(() => {
    openDocumentOptionsModal();
    Segment.trackEvent(DOCUMENT_OPTIONS_CONFIG_START, {
      docType,
    });
  }, [docType, openDocumentOptionsModal]);

  const shouldShowDocumentOptionsButton = userHasConfigurationRole && Object.keys(docTypeToOptionsModalMap).includes(identifier);

  return (
    <>
      <div className={styles.cell} data-shippy-highlight-id={identifier}>
        <FormattedMessage {...documentNameTranslation} />
      </div>
      <div className={styles.cell}>
        <Button onClick={onOpenPrintToModal} theme='link'>
          {isConnect ? printToSettingName : <FormattedMessage {...printToMessage} /> /* use the printer name if connect */}
        </Button>
      </div>
      <div className={styles.cell}>
        <DocumentFormatColumn docType={identifier} />
      </div>
      <div className={styles.cell}>
        {shouldShowDocumentOptionsButton && (
          <Button onClick={onOpenDocumentOptionsModal} theme='link'>
            <FormattedMessage {...messages.documentOptions} />
          </Button>
        )}
      </div>
      {isPrintToModalOpen && <PrintToModal docType={docType} onClose={closePrintToModal} />}
      {isDocumentOptionsModalOpen && <DocumentOptionsModal docType={docType.identifier} onClose={closeDocumentOptionsModal} />}
    </>
  );
};

PrintSettingRow.propTypes = {
  docType: PropTypes.oneOf(Object.values(DocumentType)).isRequired,
};

export { PrintSettingRow };
